/**
 * ScheduleModal — public-facing time-series view of the Schedule KPI.
 *
 * DEV-1080, Chunk B. Opens when any viewer clicks the Schedule shape on
 * the Mini-Project Progress tab. Status is the canonical record of
 * truth for planned/actual %; this modal is a read-only time-series
 * view over filed statuses, NOT an independent edit surface. To change
 * the numbers, the Lead opens the Status writer.
 *
 * Composition (top to bottom):
 *   - Header: "Schedule" + current gap chip + close ×
 *   - Current snapshot: planned %, actual %, gap chip with color band
 *   - SVG line chart: planned (dashed) vs actual (solid) over filed dates
 *   - Recent updates table: most recent 8 statuses with date + planned
 *     + actual + per-row gap chip
 *   - Footer hint (Lead-only): "To change these numbers, open the
 *     Status update modal" with explanation
 *
 * Threshold band rule matches StatusWriter / SPEC_MINI_PROJECT_PROGRESS:
 *   within 5 pts green, 5–15 yellow, > 15 or past deadline red.
 *
 * Strings localized via `pages:miniProject.kpiModals.schedule.*`
 * (Chunk D Phase 4 sweep, 2026-05-22).
 */

import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { MiniProjectStatusSummary } from "../../api/miniProjectStatuses";
import {
  classifyScheduleGap,
  computeScheduleThresholds,
  DEFAULT_SCHEDULE_THRESHOLDS,
} from "../../Data/MiniProjectConfig/scheduleThresholds";
import styles from "./ScheduleModal.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  onClose: () => void;
  /** Summaries newest-first (server already sorts this way). Modal
   *  reverses for left-to-right time order on the chart. */
  summaries: MiniProjectStatusSummary[];
  /** Project duration in days, for auto-scaling thresholds.
   *  Null when start/end dates aren't set — falls back to defaults. */
  durationDays?: number | null;
  /** Whether the current viewer can edit the project (Lead). Drives the
   *  footer hint about how to change the numbers. */
  isLead: boolean;
}

/* ── Helpers ───────────────────────────────────────────────────── */

function formatShortDate(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatLongDate(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/* Schedule classifier is imported from the shared helper module
 * (src/Data/MiniProjectConfig/scheduleThresholds.ts). Thresholds
 * auto-scale by project duration per DEV-1080 Chunk B. */

/* ── Component ─────────────────────────────────────────────────── */

const ScheduleModal: React.FC<Props> = ({
  open,
  onClose,
  summaries,
  durationDays = null,
  isLead,
}) => {
  const { t } = useAppTranslation("pages");
  const thresholds = useMemo(
    () => computeScheduleThresholds(durationDays),
    [durationDays],
  );
  // Escape key + click-outside close handled below.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Order ascending for the chart (oldest → newest, left → right).
  // Summaries come from the server newest-first; reverse here so we
  // don't pay for the swap on every render of the table below.
  const chrono = useMemo(
    () => [...summaries].reverse(),
    [summaries],
  );

  const latest = summaries[0];
  const gap = latest
    ? classifyScheduleGap(Number(latest.plannedPct), Number(latest.actualPct), thresholds)
    : null;

  if (!open) return null;

  const modal = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={t("miniProject.kpiModals.schedule.title")}>
        {/* ── Header ── */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.title}>{t("miniProject.kpiModals.schedule.title")}</span>
            {gap && (
              <span className={`${styles.gapChip} ${styles[`gapChip_${gap.color}`]}`}>
                <span className={styles.gapDot} aria-hidden="true" />
                <span>{gap.label}</span>
              </span>
            )}
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("miniProject.kpiModals.schedule.close")}>
            ×
          </button>
        </header>

        {/* ── Body ── */}
        <div className={styles.body}>
          {chrono.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>{t("miniProject.kpiModals.schedule.empty.title")}</p>
              <p className={styles.emptyHint}>
                {t("miniProject.kpiModals.schedule.empty.hint")}
                {isLead
                  ? ` ${t("miniProject.kpiModals.schedule.empty.leadCta")}`
                  : ` ${t("miniProject.kpiModals.schedule.empty.publicHint")}`}
              </p>
            </div>
          ) : (
            <>
              {/* Current snapshot card */}
              {latest && (
                <div className={styles.snapshot}>
                  <div className={styles.snapshotItem}>
                    <span className={styles.snapshotLabel}>{t("miniProject.kpiModals.schedule.snapshot.planned")}</span>
                    <span className={styles.snapshotValue}>
                      {Number(latest.plannedPct).toFixed(0)}%
                    </span>
                  </div>
                  <div className={styles.snapshotItem}>
                    <span className={styles.snapshotLabel}>{t("miniProject.kpiModals.schedule.snapshot.actual")}</span>
                    <span className={styles.snapshotValue}>
                      {Number(latest.actualPct).toFixed(0)}%
                    </span>
                  </div>
                  <div className={styles.snapshotItem}>
                    <span className={styles.snapshotLabel}>{t("miniProject.kpiModals.schedule.snapshot.asOf")}</span>
                    <span className={styles.snapshotValue}>
                      {formatLongDate(latest.reportingDate)}
                    </span>
                  </div>
                </div>
              )}

              {/* Time-series chart */}
              <div className={styles.chartWrap}>
                <div className={styles.chartHeader}>
                  <span className={styles.chartTitle}>{t("miniProject.kpiModals.schedule.chart.title")}</span>
                  <span className={styles.chartLegend}>
                    <span className={styles.legendItem}>
                      <span className={styles.legendDashed} aria-hidden="true" />
                      {t("miniProject.kpiModals.schedule.legend.planned")}
                    </span>
                    <span className={styles.legendItem}>
                      <span className={styles.legendSolid} aria-hidden="true" />
                      {t("miniProject.kpiModals.schedule.legend.actual")}
                    </span>
                  </span>
                </div>
                <ScheduleChart chrono={chrono} chartAriaLabel={t("miniProject.kpiModals.schedule.chart.aria")} />
              </div>

              {/* Recent updates table */}
              <div className={styles.tableWrap}>
                <div className={styles.tableTitle}>{t("miniProject.kpiModals.schedule.recentUpdates")}</div>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t("miniProject.kpiModals.schedule.table.date")}</th>
                      <th>{t("miniProject.kpiModals.schedule.table.planned")}</th>
                      <th>{t("miniProject.kpiModals.schedule.table.actual")}</th>
                      <th>{t("miniProject.kpiModals.schedule.table.gap")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaries.slice(0, 8).map((s) => {
                      const rowGap = classifyScheduleGap(
                        Number(s.plannedPct),
                        Number(s.actualPct),
                        thresholds,
                      );
                      return (
                        <tr key={s.statusGuid}>
                          <td>{formatShortDate(s.reportingDate)}</td>
                          <td className={styles.numCell}>
                            {Number(s.plannedPct).toFixed(0)}%
                          </td>
                          <td className={styles.numCell}>
                            {Number(s.actualPct).toFixed(0)}%
                          </td>
                          <td>
                            <span className={`${styles.gapChip} ${styles[`gapChip_${rowGap.color}`]}`}>
                              <span className={styles.gapDot} aria-hidden="true" />
                              {rowGap.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Threshold legend / how-to-edit hint */}
              <div className={styles.thresholdLegend}>
                <span style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                  {t("miniProject.kpiModals.schedule.threshold.label")}
                </span>{" "}
                {t("miniProject.kpiModals.schedule.threshold.green", { pts: thresholds.greenMaxPct.toFixed(0) })}{" "}
                <span style={{ color: "var(--color-success)" }}>
                  {t("miniProject.kpiModals.schedule.threshold.greenColor")}
                </span> ·{" "}
                {t("miniProject.kpiModals.schedule.threshold.yellow", {
                  lo: thresholds.greenMaxPct.toFixed(0),
                  hi: thresholds.yellowMaxPct.toFixed(0),
                })}{" "}
                <span style={{ color: "var(--color-warning)" }}>
                  {t("miniProject.kpiModals.schedule.threshold.yellowColor")}
                </span> ·{" "}
                {t("miniProject.kpiModals.schedule.threshold.red", { pts: thresholds.yellowMaxPct.toFixed(0) })}{" "}
                <span style={{ color: "var(--color-error)" }}>
                  {t("miniProject.kpiModals.schedule.threshold.redColor")}
                </span>
                {durationDays !== null && (thresholds.greenMaxPct > DEFAULT_SCHEDULE_THRESHOLDS.greenMaxPct
                  || thresholds.yellowMaxPct > DEFAULT_SCHEDULE_THRESHOLDS.yellowMaxPct) && (
                  <span style={{ color: "var(--text-tertiary)", marginInlineStart: 6 }}>
                    {t("miniProject.kpiModals.schedule.threshold.widened", { days: durationDays.toFixed(0) })}
                  </span>
                )}
              </div>
              {isLead && (
                <div className={styles.leadHint}>
                  {t("miniProject.kpiModals.schedule.leadHint")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ScheduleModal;

/* ── Inline SVG chart ──────────────────────────────────────────────
 *
 * Hand-rolled rather than pulling in a chart library. The codebase has
 * no chart dependency today and the shape is simple enough that a CDN
 * import isn't justified for two lines over ~10 data points.
 *
 * Renders two polylines over a fixed-aspect SVG viewBox so the chart
 * scales cleanly inside the modal regardless of viewport. Y axis is
 * 0–100 percent; X axis maps reporting dates to evenly-spaced points
 * (NOT calendar-scaled — keeps the visual stable when there are gaps
 * between status filings).
 */

const CHART_WIDTH = 560;
const CHART_HEIGHT = 200;
const CHART_PAD_X = 32;
const CHART_PAD_Y = 18;

function ScheduleChart({ chrono, chartAriaLabel }: { chrono: MiniProjectStatusSummary[]; chartAriaLabel: string }) {
  const points = useMemo(() => {
    const n = chrono.length;
    if (n === 0) return { planned: "", actual: "", xs: [] as { x: number; iso: string }[] };
    const usableW = CHART_WIDTH - CHART_PAD_X * 2;
    const usableH = CHART_HEIGHT - CHART_PAD_Y * 2;
    // When there's only one point, center it instead of dividing by zero.
    const stride = n > 1 ? usableW / (n - 1) : 0;
    const planned: string[] = [];
    const actual: string[] = [];
    const xs: { x: number; iso: string }[] = [];
    for (let i = 0; i < n; i++) {
      const s = chrono[i];
      const x = CHART_PAD_X + (n > 1 ? i * stride : usableW / 2);
      const plannedPct = Math.max(0, Math.min(100, Number(s.plannedPct)));
      const actualPct = Math.max(0, Math.min(100, Number(s.actualPct)));
      const yP = CHART_PAD_Y + usableH * (1 - plannedPct / 100);
      const yA = CHART_PAD_Y + usableH * (1 - actualPct / 100);
      planned.push(`${x.toFixed(1)},${yP.toFixed(1)}`);
      actual.push(`${x.toFixed(1)},${yA.toFixed(1)}`);
      xs.push({ x, iso: s.reportingDate });
    }
    return {
      planned: planned.join(" "),
      actual: actual.join(" "),
      xs,
    };
  }, [chrono]);

  // Pick at most ~5 evenly-distributed x-axis tick labels so they
  // don't overlap on narrow data sets or pile up on dense ones.
  const xTicks = useMemo(() => {
    const xs = points.xs;
    const n = xs.length;
    if (n === 0) return [];
    if (n <= 5) return xs;
    const step = Math.ceil(n / 5);
    const out: typeof xs = [];
    for (let i = 0; i < n; i += step) out.push(xs[i]);
    if (out[out.length - 1] !== xs[n - 1]) out.push(xs[n - 1]);
    return out;
  }, [points]);

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      className={styles.chart}
      role="img"
      aria-label={chartAriaLabel}
    >
      {/* Y gridlines at 0/25/50/75/100% */}
      {[0, 25, 50, 75, 100].map((pct) => {
        const usableH = CHART_HEIGHT - CHART_PAD_Y * 2;
        const y = CHART_PAD_Y + usableH * (1 - pct / 100);
        return (
          <g key={pct}>
            <line
              x1={CHART_PAD_X}
              x2={CHART_WIDTH - CHART_PAD_X}
              y1={y}
              y2={y}
              className={styles.gridline}
            />
            <text
              x={CHART_PAD_X - 6}
              y={y + 3}
              textAnchor="end"
              className={styles.axisLabel}
            >
              {pct}
            </text>
          </g>
        );
      })}

      {/* Planned (dashed) */}
      {points.planned && (
        <polyline
          points={points.planned}
          fill="none"
          className={styles.plannedLine}
        />
      )}
      {/* Actual (solid) */}
      {points.actual && (
        <polyline
          points={points.actual}
          fill="none"
          className={styles.actualLine}
        />
      )}

      {/* Single-point fallback: draw small dots so a lone status is still visible */}
      {points.xs.length === 1 && (
        <g>
          {(() => {
            const usableH = CHART_HEIGHT - CHART_PAD_Y * 2;
            const x = points.xs[0].x;
            const plannedPct = Math.max(0, Math.min(100, Number(chrono[0].plannedPct)));
            const actualPct = Math.max(0, Math.min(100, Number(chrono[0].actualPct)));
            const yP = CHART_PAD_Y + usableH * (1 - plannedPct / 100);
            const yA = CHART_PAD_Y + usableH * (1 - actualPct / 100);
            return (
              <>
                <circle cx={x} cy={yP} r={4} className={styles.plannedDot} />
                <circle cx={x} cy={yA} r={4} className={styles.actualDot} />
              </>
            );
          })()}
        </g>
      )}

      {/* X-axis labels */}
      {xTicks.map((t, i) => (
        <text
          key={`${i}-${t.iso}`}
          x={t.x}
          y={CHART_HEIGHT - 4}
          textAnchor="middle"
          className={styles.axisLabel}
        >
          {formatShortDate(t.iso)}
        </text>
      ))}
    </svg>
  );
}
